/**
 * Privacy & data tab — GDPR-shaped controls.
 *
 *   - Export: downloads a JSON snapshot of every account, transaction,
 *     recurring rule, and paid receipt the user owns. Right of access
 *     under UK GDPR Art. 15.
 *   - Soft-delete: marks the account `deleted_at` + schedules a 30-day
 *     purge. Logging back in within the grace window auto-restores.
 *     Owner accounts are blocked server-side (foot-gun protection).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, Trash2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

const PrivacyTab = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');

  const exportData = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/account/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `forge-ledger-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export downloaded.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not export your data.');
    } finally {
      setExporting(false);
    }
  };

  const submitDelete = async (e) => {
    e.preventDefault();
    if (confirmText !== CONFIRM_PHRASE) {
      toast.error(`Type exactly: ${CONFIRM_PHRASE}`);
      return;
    }
    if (!password) {
      toast.error('Enter your current password.');
      return;
    }
    setDeleting(true);
    try {
      await axios.post(`${API}/account/soft-delete`, {
        current_password: password,
        confirm: confirmText,
      }, { withCredentials: true });
      toast.success('Account deleted. We hold your data for 30 days — log back in within that window to restore everything.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete account.');
      setDeleting(false);
    }
  };

  const isOwner = user?.role === 'owner';

  return (
    <div className="space-y-6 max-w-2xl" data-testid="privacy-tab">
      {/* Export */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="privacy-export-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-[#0052FF]" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-[#020617] mb-1">Export your data</h2>
            <p className="text-sm text-[#64748B] mb-4">Download a JSON snapshot of every account, transaction, recurring rule, and paid receipt we hold for you. Right of access under UK GDPR.</p>
            <Button
              onClick={exportData}
              disabled={exporting}
              className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
              data-testid="privacy-export-button"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting…' : 'Download JSON export'}
            </Button>
          </div>
        </div>
      </div>

      {/* Soft-delete */}
      <div className="bg-white border border-[#FECACA] rounded-xl p-6" data-testid="privacy-delete-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FEE2E2] flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-[#991B1B]" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-[#020617] mb-1">Delete account</h2>
            <p className="text-sm text-[#64748B] mb-1">
              We soft-delete first — your data is held for <strong>30 days</strong> in case you change your mind. Logging back in within that window restores everything automatically.
            </p>
            <p className="text-xs text-[#94A3B8] mb-4 inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> After 30 days the purge runs and data is gone for good.
            </p>

            {isOwner && (
              <div className="rounded-md bg-[#FEF3C7] border border-[#FDE68A] p-3 mb-4 text-xs text-[#92400E]" data-testid="privacy-delete-owner-block">
                Owner accounts cannot be soft-deleted. Transfer the Owner role first.
              </div>
            )}

            {!showDelete ? (
              <div className="flex justify-end">
                <Button
                  onClick={() => setShowDelete(true)}
                  disabled={isOwner}
                  className="bg-white border border-[#FECACA] text-[#991B1B] hover:bg-[#FEE2E2]"
                  data-testid="privacy-delete-start-button"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Delete my account
                </Button>
              </div>
            ) : (
              <form onSubmit={submitDelete} className="space-y-4" data-testid="privacy-delete-form">
                <div>
                  <Label htmlFor="confirm-phrase">
                    Type <code className="px-1 bg-[#F1F5F9] rounded text-[#020617]">{CONFIRM_PHRASE}</code> to confirm
                  </Label>
                  <Input
                    id="confirm-phrase"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="mt-1.5"
                    placeholder={CONFIRM_PHRASE}
                    autoComplete="off"
                    required
                    data-testid="privacy-delete-confirm-input"
                  />
                </div>
                <div>
                  <Label htmlFor="delete-password">Current password</Label>
                  <Input
                    id="delete-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5"
                    autoComplete="current-password"
                    required
                    data-testid="privacy-delete-password-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={deleting || confirmText !== CONFIRM_PHRASE}
                    className="bg-[#DC2626] text-white hover:bg-[#991B1B]"
                    data-testid="privacy-delete-submit-button"
                  >
                    {deleting ? 'Deleting…' : 'Delete account'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => { setShowDelete(false); setConfirmText(''); setPassword(''); }}
                    className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyTab;
